<template>
  <v-container fluid>
    <div class="zone-management">
      <!-- Header -->
      <div class="d-flex justify-space-between align-center mb-6">
        <div>
          <h1 class="text-h4 font-weight-bold">{{ $t("zone_management") }}</h1>
          <p class="text-subtitle-1 grey--text">
            {{ $t("zone_management_desc") }}
          </p>
        </div>
        <v-btn color="primary" depressed class="rounded-lg" @click="openAddZoneDialog">
          <v-icon left>fal fa-plus</v-icon>
          {{ $t("add_zone") }}
        </v-btn>
      </div>

      <!-- Zone Statistics Overview -->
      <v-row class="mb-6">
        <v-col cols="12" md="4">
          <v-card class="stat-card">
            <v-card-text class="text-center">
              <v-icon color="primary" size="32" class="mb-2"
                >fal fa-layer-group</v-icon
              >
              <div class="text-h4 font-weight-bold primary--text">
                {{ totalZones }}
              </div>
              <div class="text-caption grey--text">
                {{ $t("total_zones") }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="4">
          <v-card class="stat-card">
            <v-card-text class="text-center">
              <v-icon color="success" size="32" class="mb-2"
                >fal fa-check-circle</v-icon
              >
              <div class="text-h4 font-weight-bold success--text">
                {{ zonesWithTables }}
              </div>
              <div class="text-caption grey--text">
                {{ $t("zones_with_tables") }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="4">
          <v-card class="stat-card">
            <v-card-text class="text-center">
              <v-icon color="warning" size="32" class="mb-2"
                >fal fa-exclamation-circle</v-icon
              >
              <div class="text-h4 font-weight-bold warning--text">
                {{ zonesWithoutTables }}
              </div>
              <div class="text-caption grey--text">
                {{ $t("zones_without_tables") }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Zones List -->
      <v-card>
        <v-card-title>
          <span>{{ $t("table_zones") }}</span>
        </v-card-title>

        <v-data-table
          :headers="headers"
          :items="zones"
          :loading="loading"
          class="elevation-0"
          :no-data-text="$t('no_data')"
        >
          <!-- Zone Name -->
          <template v-slot:item.name="{ item }">
            <div class="d-flex align-center">
              <v-icon color="primary" size="20" class="mr-3">
                fal fa-layer-group
              </v-icon>
              <div>
                <div class="font-weight-medium">{{ item.name }}</div>
                <div v-if="item.description" class="text-caption grey--text">
                  {{ item.description }}
                </div>
              </div>
            </div>
          </template>

          <!-- Table Count -->
          <template v-slot:item.tables_count="{ item }">
            <v-chip
              :color="item.tables_count > 0 ? 'success' : 'grey'"
              text-color="white"
              small
            >
              <v-icon small left>fal fa-table</v-icon>
              {{ item.tables_count || 0 }}
            </v-chip>
          </template>

          <!-- Actions -->
          <template v-slot:item.actions="{ item }">
            <div class="d-flex gap-1">
              <v-btn icon small @click="editZone(item)" :title="$t('edit')">
                <v-icon small>fal fa-edit</v-icon>
              </v-btn>
              <v-btn
                icon
                small
                color="error"
                @click="deleteZone(item)"
                :title="$t('delete')"
              >
                <v-icon small>fal fa-trash</v-icon>
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-card>

      <!-- Add/Edit Zone Dialog -->
      <app-custom-dialog
        v-model="showZoneDialog"
        :title="editingZone ? 'edit_zone' : 'add_zone'"
        :footer="true"
        width="500px"
        :submit-text="editingZone ? 'update' : 'save'"
        @submit="saveZone"
      >
        <div slot="activator">
          <v-form ref="zoneForm" v-model="validForm">
            <v-row>
              <v-col cols="12">
                <TTextField
                  v-model="zoneForm.name"
                  label="zone_name"
                  :error-messages="validateZoneName()"
                  @input="validateZoneName"
                />
              </v-col>
              <v-col cols="12">
                <TextArea v-model="zoneForm.description" label="description" />
              </v-col>
            </v-row>
          </v-form>
        </div>
      </app-custom-dialog>
    </div>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import TTextField from "@/components/global/TextField.vue";
import TextArea from "@/components/global/TextArea.vue";
import { toast, confirm } from "@/controllers/alert";

export default {
  name: "ZoneManagement",
  components: {
    TTextField,
    TextArea,
  },
  data() {
    return {
      loading: false,
      saving: false,
      showZoneDialog: false,
      editingZone: null,
      validForm: false,
      zones: [],
      zoneForm: {
        name: "",
        description: "",
      },
    };
  },
  computed: {
    ...mapGetters({
      user: "auth/user",
    }),
    totalZones() {
      return this.zones.length;
    },
    zonesWithTables() {
      return this.zones.filter((zone) => (zone.tables_count || 0) > 0).length;
    },
    zonesWithoutTables() {
      return this.zones.filter((zone) => (zone.tables_count || 0) === 0).length;
    },
    headers() {
      return [
        { text: this.$t("zone_name"), value: "name", sortable: true },
        { text: this.$t("table_count"), value: "tables_count", sortable: true },
        {
          text: this.$t("actions"),
          value: "actions",
          sortable: false,
          width: 100,
        },
      ];
    },
  },
  methods: {
    validateZoneName() {
      return this.zoneForm.name ? [] : [this.$t("field_required")];
    },
    async loadData() {
      this.loading = true;
      try {
        const { data } = await this.$http.get("table_zones");

        if (data && data.status && data.data) {
          this.zones = data.data;
        } else {
          this.zones = [];
        }
      } catch (error) {
        console.error("Error loading zones:", error);
        toast(this, "error_loading_zones", "error");
      } finally {
        this.loading = false;
      }
    },
    openAddZoneDialog() {
      this.editingZone = null;
      this.zoneForm = {
        name: "",
        description: "",
      };
      this.showZoneDialog = true;
    },
    editZone(zone) {
      this.editingZone = zone;
      this.zoneForm = {
        name: zone.name,
        description: zone.description || "",
      };
      this.showZoneDialog = true;
    },
    async saveZone() {
      // Validate
      if (!this.zoneForm.name || this.zoneForm.name.trim() === "") {
        toast(this, "field_required", "error");
        return;
      }

      this.saving = true;
      try {
        const formData = {
          name: this.zoneForm.name.trim(),
          description: this.zoneForm.description || "",
          store_id: this.user.stores.id,
          branch_id: this.user.branch_id,
        };

        if (this.editingZone) {
          // Update
          const { data } = await this.$http.put(
            `table_zones/${this.editingZone.id}`,
            formData
          );

          if (data && data.status) {
            toast(this, "zone_updated_success", "success");
            await this.loadData();
            this.showZoneDialog = false;
            this.resetForm();
          }
        } else {
          // Create
          const { data } = await this.$http.post("table_zones", formData);

          if (data && data.status) {
            toast(this, "zone_created_success", "success");
            await this.loadData();
            this.showZoneDialog = false;
            this.resetForm();
          }
        }
      } catch (error) {
        console.error("Error saving zone:", error);
        const errorMessage =
          error.response?.data?.message || "error_saving_zone";
        toast(this, errorMessage, "error");
      } finally {
        this.saving = false;
      }
    },
    async deleteZone(zone) {
      const { isConfirmed } = await confirm({
        props: this,
        title: "delete_zone",
        text: "delete_zone_confirm",
      });

      if (!isConfirmed) return;

      try {
        const { data } = await this.$http.delete(`table_zones/${zone.id}`);

        if (data && data.status) {
          toast(this, "zone_deleted_success", "success");
          await this.loadData();
        }
      } catch (error) {
        console.error("Error deleting zone:", error);
        const errorMessage =
          error.response?.data?.message || this.$t("error_deleting_zone");
        toast(this, errorMessage, "error");
      }
    },
    resetForm() {
      this.zoneForm = {
        name: "",
        description: "",
      };
      this.editingZone = null;
      if (this.$refs.zoneForm) {
        this.$refs.zoneForm.resetValidation();
      }
    },
  },
  async created() {
    await this.loadData();
  },
};
</script>

<style scoped>
.zone-management {
  max-width: 1400px;
  margin: 0 auto;
}

.stat-card {
  border-radius: 12px;
  transition: all 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.gap-1 {
  gap: 4px;
}

/* Mobile responsiveness */
@media (max-width: 600px) {
  .zone-management {
    padding: 8px;
  }

  .d-flex.justify-space-between.align-center {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
}
</style>
